import {
  forwardRef,
  type ChangeEventHandler,
  type CSSProperties,
  type FocusEventHandler,
  type ForwardedRef,
  type KeyboardEventHandler,
} from 'react';
import { InputMask } from '@react-input/mask';
import classNames from 'classnames';
import * as styles from './Input.module.scss';

const CENTS_STEP_VALUE = '.01';

type InputType = 'text' | 'phone' | 'textArea' | 'price';

interface InputProps {
  id?: string;
  label: string;
  value?: string | number;
  name: string;
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  placeholder?: string;
  disabled?: boolean;
  style?: CSSProperties;
  className?: string;
  containerClassName?: string;
  required?: boolean;
  propRef?: unknown;
  type?: InputType;
  characterLimit?: number;
  error?: string;
  datatestid?: string;
  dataRecurly?: string;
  inputMask?: string;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  function ForwardedInput(
    {
      id,
      label,
      value,
      name,
      onChange,
      placeholder,
      disabled,
      style,
      className,
      required,
      type = 'text',
      characterLimit,
      error,
      datatestid,
      dataRecurly,
      inputMask,
      onBlur,
      containerClassName,
      onKeyDown,
      onFocus,
    },
    ref,
  ) {
    const resolveInputByType = () => {
      switch (type) {
        case 'text': {
          return (
            <input
              id={id}
              className={classNames(className, styles.input, { [styles.error]: error })}
              data-testid={datatestid}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              type="text"
              required={required}
              disabled={disabled}
              maxLength={characterLimit}
              ref={ref as ForwardedRef<HTMLInputElement>}
              data-recurly={dataRecurly}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
            />
          );
        }
        case 'phone': {
          return (
            <InputMask
              id={id}
              className={classNames(className, styles.input, { [styles.error]: error })}
              data-testid={datatestid}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              type="text"
              required={required}
              disabled={disabled}
              maxLength={characterLimit}
              ref={ref as ForwardedRef<HTMLInputElement>}
              data-recurly={dataRecurly}
              mask={inputMask}
              key={inputMask}
              replacement={{ _: /\d/ }}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
            />
          );
        }
        case 'textArea': {
          return (
            <textarea
              id={id}
              className={classNames(className, styles.input, styles.textArea, {
                [styles.error]: error,
              })}
              name={name}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              disabled={disabled}
              required={required}
              maxLength={characterLimit}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
              ref={ref as ForwardedRef<HTMLTextAreaElement>}
            />
          );
        }
        case 'price': {
          return (
            <div className={styles.priceInput}>
              <i className="far fa-dollar-sign" />
              <input
                id={id}
                className={classNames(className, styles.input, { [styles.error]: error })}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                type="number"
                step={CENTS_STEP_VALUE}
                min="0"
                pattern="^\d*(\.\d{0,2})?$"
                required={required}
                disabled={disabled}
                maxLength={characterLimit}
                ref={ref as ForwardedRef<HTMLInputElement>}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                onFocus={onFocus}
              />
            </div>
          );
        }
      }
    };

    return (
      <div className={classNames(styles.container, containerClassName)} style={style}>
        {label && <label htmlFor={id || name}>{label}</label>}
        {resolveInputByType()}
        {/*color attribute error must be handled separately due to design of input*/}
        {error && label !== 'Color Attribute' && (
          <p className={styles.errorMessage} data-testid={`${label}-error`}>
            {error}
          </p>
        )}
      </div>
    );
  },
);

export default Input;
